
export const outputJSONFormat_JWT = {
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://example.com/schemas/jwt-automation-pack.schema.json",
  "title": "JWTAutomationPack",
  "description": "Implementation-ready Jira Workflow Toolbox automation pack derived from workflow evidence.",
  "type": "object",
  "additionalProperties": false,
  "properties": {
    "pack_id": { "type": "string" },
    "title": { "type": "string" },
    "generated_at": { "type": "string", "format": "date-time" },
    "issue_type": { "type": "string" },
    "automation_opportunities": {
      "type": "array",
      "minItems": 1,
      "items": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "id": { "type": "string" },
          "title": { "type": "string" },
          "fix_theme": { "type": "string", "description": "Kebab-case identifier of the underlying process fix, shared across packs." },
          "priority": { "type": "string", "enum": ["high", "medium", "low"] },
          "problem_statement": { "type": "string" },
          "goal": { "type": "string" },
          "applies_to": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "project_keys": { "type": "array", "items": { "type": "string" } },
              "issue_types": { "type": "array", "items": { "type": "string" } },
              "workflow_names": { "type": "array", "items": { "type": "string" } }
            }
          },
          "related_transitions": { "type": "array", "items": { "type": "string" } },
          "related_variants": { "type": "array", "items": { "type": "string" } },
          "evidence_signals": { "type": "array", "items": { "type": "string" } },
          "jwt_implementation": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "summary": { "type": "string" },
              "preconditions": { "type": "array", "items": { "type": "string" } },
              "configuration_steps": { "type": "array", "items": { "type": "string" } },
              "transition_configuration": {
                "type": "array",
                "items": {
                  "type": "object",
                  "additionalProperties": false,
                  "properties": {
                    "transition_name": { "type": "string" },
                    "validators": { "type": "array", "items": { "type": "string" } },
                    "conditions": { "type": "array", "items": { "type": "string" } },
                    "post_functions": { "type": "array", "items": { "type": "string" } }
                  },
                  "required": ["transition_name"]
                }
              },
              "scheduled_rules": {
                "type": "array",
                "items": {
                  "type": "object",
                  "additionalProperties": false,
                  "properties": {
                    "name": { "type": "string" },
                    "cron_expression": { "type": "string" },
                    "schedule_description": { "type": "string" },
                    "jql_scope": { "type": "string" },
                    "actions": { "type": "array", "items": { "type": "string" } }
                  },
                  "required": ["name", "jql_scope"]
                }
              },
              "expression_snippets": {
                "type": "array",
                "items": {
                  "type": "object",
                  "additionalProperties": false,
                  "properties": {
                    "purpose": { "type": "string" },
                    "expression": { "type": "string" }
                  },
                  "required": ["purpose", "expression"]
                }
              }
            },
            "required": ["summary", "preconditions", "configuration_steps"]
          },
          "success_criteria": { "type": "array", "items": { "type": "string" } },
          "expected_impact": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "impact_summary": { "type": "string" },
              "kpis_affected": { "type": "array", "items": { "type": "string" } },
              "impact_direction": { "type": "string", "enum": ["positive", "neutral", "negative", "unknown"] }
            },
            "required": ["impact_summary"]
          },
          "risk_and_considerations": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "risk_level": { "type": "string", "enum": ["low", "medium", "high"] },
              "risks": { "type": "array", "items": { "type": "string" } },
              "rollout_recommendations": { "type": "array", "items": { "type": "string" } }
            },
            "required": ["risk_level"]
          }
        },
        "required": ["id", "title", "problem_statement", "goal", "jwt_implementation", "expected_impact", "risk_and_considerations"]
      }
    }
  },
  "required": ["pack_id", "title", "generated_at", "automation_opportunities"]
}

export const outputJSONFormat_Jira = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://example.com/schemas/a4j-native-pack.schema.json',
  title: 'A4JNativePack',
  description: 'Container for native Jira Automation configurations derived from workflow/transition analysis.',
  type: 'object',
  additionalProperties: false,
  properties: {
    version: { type: 'string', description: "Schema version of this pack (e.g., '1.0.0')." },
    generated_at: { type: 'string', format: 'date-time' },
    source_context: {
      type: 'object',
      additionalProperties: false,
      properties: {
        issue_type: { type: 'string' },
        project_keys: { type: 'array', items: { type: 'string' } },
        analysis_summary: { type: 'string' },
        sample_window: { type: 'string' }
      },
      required: ['issue_type']
    },
    insights: {
      type: 'object',
      additionalProperties: false,
      properties: {
        key_discovery_rules: { type: 'array', items: { type: 'string' } },
        hotspots: {
          type: 'array',
          items: {
            type: 'object',
            additionalProperties: false,
            properties: {
              name: { type: 'string' },
              transition: { type: 'string' },
              affected_pct: { type: 'number' },
              note: { type: 'string' }
            },
            required: ['name']
          }
        }
      }
    },
    rules: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          id: { type: 'string', description: 'Stable id for dedupe/versioning.' },
          name: { type: 'string' },
          description: { type: 'string' },
          fix_theme: { type: 'string', description: 'Kebab-case identifier of the underlying process fix, shared across packs.' },
          risk_level: { type: 'string', enum: ['low', 'medium', 'high'] },
          expected_impact: { type: 'string' },
          scope: {
            type: 'object',
            additionalProperties: false,
            properties: {
              projects: { type: 'array', items: { type: 'string' } },
              issue_types: { type: 'array', items: { type: 'string' } },
              rule_scope: { type: 'string', enum: ['global', 'project'] }
            }
          },
          references: {
            type: 'object',
            additionalProperties: false,
            properties: {
              related_transitions: { type: 'array', items: { type: 'string' } },
              rationale: { type: 'string' }
            }
          },
          triggers: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: {
                type: {
                  type: 'string',
                  enum: [
                    'issue_created',
                    'issue_updated',
                    'issue_transitioned',
                    'field_value_changed',
                    'comment_added',
                    'scheduled',
                    'incoming_webhook'
                  ]
                },
                config: { type: 'object', additionalProperties: true }
              },
              required: ['type']
            }
          },
          conditions: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: {
                type: {
                  type: 'string',
                  enum: [
                    'jql',
                    'status',
                    'issue_fields',
                    'user_in_group',
                    'branch_issue',
                    'advanced_compare'
                  ]
                },
                config: { type: 'object', additionalProperties: true }
              },
              required: ['type']
            }
          },
          branches: {
            type: 'array',
            items: {
              type: 'object',
              additionalProperties: false,
              properties: {
                type: { type: 'string', enum: ['current_issue', 'related_issues', 'jql'] },
                config: { type: 'object', additionalProperties: true },
                actions: { type: 'array', items: { $ref: '#/definitions/action' } }
              },
              required: ['type', 'actions']
            }
          },
          actions: { type: 'array', items: { $ref: '#/definitions/action' } },
          export: {
            type: 'object',
            additionalProperties: false,
            properties: {
              a4j_cloud: { type: 'object', additionalProperties: true },
              a4j_dc: { type: 'object', additionalProperties: true }
            }
          },
          tags: { type: 'array', items: { type: 'string' } }
        },
        required: ['id', 'name', 'triggers', 'actions']
      }
    }
  },
  required: ['version', 'generated_at', 'source_context', 'rules'],
  definitions: {
    action: {
      type: 'object',
      additionalProperties: false,
      properties: {
        type: {
          type: 'string',
          enum: [
            'transition_issue',
            'edit_issue',
            'add_comment',
            'assign_issue',
            'send_email',
            'create_subtask',
            'link_issue',
            're_open_issue',
            'set_priority',
            'set_field',
            'set_label'
          ]
        },
        config: { type: 'object', additionalProperties: true }
      },
      required: ['type']
    }
  }
};

export const outputJSONFormat_ScriptRunner = 
{
  "title": "ScriptRunnerAutomationPack",
  "type": "object",
  "additionalProperties": false,
  "properties": {
    "pack_id": { "type": "string" },
    "jira_deployment": { "type": "string", "enum": ["Cloud","Cloud"] },
    "generated_at": { "type": "string" },
    "source": {
      "type": "object",
      "additionalProperties": false,
      "properties": {
        "issue_type": { "type": "string" },
        "projects": { "type": "array", "items": { "type": "string" } }
      },
      "required": ["issue_type"]
    },
    "discovery_rules_summary": { "type": "array", "items": { "type": "string" } },
    "rules": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "rule_id": { "type": "string" },
          "name": { "type": "string" },
          "intent": { "type": "string" },
          "fix_theme": { "type": "string", "description": "Kebab-case identifier of the underlying process fix, shared across packs." },
          "trigger": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "type": { "type": "string", "enum": ["listener","scheduled"] },
              "events": { "type": "array", "items": { "type": "string" } },
              "schedule": { "type": "string" }
            },
            "required": ["type"]
          },
          "jql_scope": { "type": "string" },
          "when": { "type": "array", "items": { "type": "string" } },
          "actions": { "type": "array", "items": { "type": "string" } },
          "script": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "language": { "type": "string", "enum": ["groovy"] },
              "pseudo": { "type": "string", "description": "Plain English summary of what the script does." },
              "code": { "type": "string", "description": "Actual runnable ScriptRunner for Jira Cloud Groovy code (Script Console, Script Listener or Scheduled Job) using the built-in Unirest-style REST helpers, ready to paste into ScriptRunner." }
            },
            "required": ["language","pseudo","code"]
          },
          "references": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "transitions": { "type": "array", "items": { "type": "string" } },
              "variants": { "type": "array", "items": { "type": "string" } }
            }
          },
          "telemetry": { "type": "array", "items": { "type": "string" } },
          "risk_notes": { "type": "array", "items": { "type": "string" } }
        },
        "required": ["rule_id","name","intent","trigger","script"]
      }
    }
  },
  "required": ["pack_id","jira_deployment","generated_at","source","rules"]
}


export const outputJSONFormat_JMWE = {
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://example.com/schemas/jmwe-automation-pack.min.schema.json",
  "title": "JMWEAutomationPack",
  "description": "Schema for defining JMWE (Jira Misc Workflow Extensions) workflow optimizations derived from process-mining.",
  "type": "object",
  "additionalProperties": false,
  "properties": {
    "pack_id": { "type": "string", "description": "Stable identifier for this pack." },
    "title": { "type": "string" },
    "generated_at": { "type": "string", "format": "date-time" },
    "jira_deployment": { "type": "string", "enum": ["cloud", "datacenter"], "default": "cloud" },
    "project_keys": {
      "type": "array",
      "items": { "type": "string", "pattern": "^[A-Z][A-Z0-9]+$" },
      "minItems": 1
    },
    "discovery_rules_summary": {
      "type": "array",
      "items": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "label": { "type": "string" },
          "metric": { "type": "string" },
          "value": { "type": ["number", "string"] },
          "insight": { "type": "string" }
        },
        "required": ["label", "insight"]
      }
    },
    "rules": {
      "type": "array",
      "minItems": 1,
      "items": {
        "type": "object",
        "additionalProperties": false,
        "properties": {
          "rule_id": { "type": "string" },
          "name": { "type": "string" },
          "fix_theme": { "type": "string", "description": "Kebab-case identifier of the underlying process fix, shared across packs." },
          "priority": { "type": "string", "enum": ["high", "medium", "low"], "default": "medium" },
          "applies_to": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "project_keys": { "type": "array", "items": { "type": "string" } },
              "issue_types": { "type": "array", "items": { "type": "string" } },
              "workflow_names": { "type": "array", "items": { "type": "string" } }
            }
          },
          "target_transitions": {
            "type": "array",
            "items": {
              "type": "object",
              "additionalProperties": false,
              "properties": {
                "from": { "type": "string" },
                "to": { "type": "string" },
                "label": { "type": "string" }
              },
              "required": ["from", "to"]
            }
          },
          "preconditions": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "jql": { "type": "string" },
              "notes": { "type": "string" }
            }
          },
          "jmwe_actions": {
            "type": "array",
            "minItems": 1,
            "items": {
              "type": "object",
              "additionalProperties": false,
              "properties": {
                "type": { "type": "string", "enum": ["post_function", "validator", "condition", "listener"] },
                "name": { "type": "string" },
                "config": { "type": "object" }
              },
              "required": ["type", "name"]
            }
          },
          "success_criteria": {
            "type": "array",
            "items": { "type": "string" }
          },
          "references": {
            "type": "object",
            "additionalProperties": false,
            "properties": {
              "transition_indices": { "type": "array", "items": { "type": "integer" } },
              "variant_indices": { "type": "array", "items": { "type": "integer" } }
            }
          },
          "rationale": { "type": "string" },
          "risks": { "type": "array", "items": { "type": "string" } }
        },
        "required": ["rule_id", "name", "jmwe_actions"]
      }
    }
  },
  "required": ["pack_id", "title", "generated_at", "jira_deployment", "project_keys", "rules"]
}